import type { DateOrDateTime, UTCDate } from "../date";

export interface IQualificationModel {
  id: string;
  qualification?: string;
  nameOfInstitution?: string;
  addressOfInstitution?: string;
  startOfTraining?: UTCDate;
  endOfTraining?: UTCDate;
  occupation?: string;
  professionalTitle?: string;
  professionalTitleGermanTranslation?: string;
  occupationalTitle?: string;
  diplomaIssuance?: UTCDate;
  city?: string;
  country?: string;
  finalGrade?: string;
  isRecognitionOrOccupationRelevant?: boolean;
  educationFocus?: string;
  educationDurationInMonths?: number;
  practicalTrainingInMonths?: number;
  entailsTheoreticalTraining?: boolean;
  entailsPracticalTraining?: boolean;
  entailsCompanyBasedTraining?: boolean;
  entailsSchoolBasedTraining?: boolean;
  educationalInstitutionPhone?: string;
  educationalInstitutionEmail?: string;
  educationalInstitutionWebsite?: string;
  issuingAuthorityName?: string;
  issuingAuthorityAddress?: string;
  issuingAuthorityCity?: string;
  issuingAuthorityCountry?: string;
  issuingAuthorityPhone?: string;
  issuingAuthorityEmail?: string;
  issuingAuthorityWebsite?: string;
}

export interface IExperienceModel {
  id: string;
  experienceStartDate: UTCDate;
  experienceEndDate: UTCDate;
  experienceFields: IExperienceFieldModel[];
  experienceFunction?: string;
  experienceFacility: string;
  experienceInstitution?: string;
  experienceBedCount?: string;
  experienceWorkArea?: string;
  experienceYears?: number;
  experienceMonth?: number;
  experienceCount?: number;
  experienceCoreTasks?: string;
  experienceCoreTasksDelta?: object;
  city?: string;
  country?: string;
  occupation?: string;
  occupationOther?: string;
  experienceType?: ExperienceType;
  otherActivity?: string;
  typeOfEmployment?: TypeOfEmployment[];
  mandatoryInternship?: boolean;
  workHours?: string;
  hoursPerWeek?: number;
  employerAddress?: string;
  employerPhone?: string;
  employerEmail?: string;
}

export interface IExperienceFieldModel {
  id: string;
  experienceField: string;
  experienceNotes: IQuillModel;
}

export interface IQuillModel {
  contents?: string;
  delta?: object;
}

export interface IBasicLanguageSkillModel {
  id: string;
  language?: string;
  skillLevel?: string;
  languageTrainingStatus?: LanguageTrainingStatus;
  languageLevelSource?: LanguageLevelSource;
  examInstitution?: string;
}

export interface ILanguageSkillModel extends IBasicLanguageSkillModel {
  examCertificateDate?: UTCDate;
}

export interface IFurtherEducation {
  id: string;
  startDate: UTCDate;
  endDate?: UTCDate;
  fieldOfEducation: string;
  nameOfInstitution?: string;
  totalHours?: number;
  description?: string;
  nameOfEducation?: string;
  notProfessionRelated?: boolean;
  educationalInstitutionAddress?: string;
  educationalInstitutionPhone?: string;
  educationalInstitutionEmail?: string;
  issuingAuthorityName?: string;
  issuingAuthorityAddress?: string;
  issuingAuthorityCity?: string;
  issuingAuthorityCountry?: string;
  issuingAuthorityPhone?: string;
  issuingAuthorityEmail?: string;
  issuingAuthorityWebsite?: string;
}

export interface IProcessStatusModel {
  estimatedArrivalDate?: UTCDate;
  estimatedWorkStartDate?: UTCDate;
}

export interface IAddressModel {
  id: string;
  addressType: AddressType[];
  residentSince?: UTCDate;
  address?: string;
  zipcode?: string;
  city?: string;
  region?: string;
  country?: string;
}

export interface IBankDetailsModel {
  id: string;
  // typeOfBankTransfer: string;
  isSepa: boolean;
  // countryOfBankDetail: string;
  isImmigrationCountryBankDetail: boolean;
  isCurrentAccount: boolean;
  accountHolder?: string;
  bankName?: string;

  //SEPA
  iban?: string;
  bic?: string;

  //SWIFT
  bankAccountNumber?: string;
  bankCountry?: string;
  currency?: string;

  isBic?: boolean;
  bankCode?: string;
}

export interface IOtherModel {
  wayToUs?: string;
  hobbies?: string;
  socialSkills?: string;
  itSkills?: string;
  familiarEquipment?: string;
  aboutMe?: string;
}

export interface IExternalCharacteristicsModel {
  eyeColor?: string;
  bodyHeight?: number;
}

export interface ICompetencyModel {
  id: string;
  skillId: string;
  proficiency: string;
  additionalInformation: string;
}

export interface ICompetenciesModel {
  knowledges: ICompetencyModel[];
  skills: ICompetencyModel[];
  customKnowledges: ICompetencyModel[];
  customSkills: ICompetencyModel[];
}

export interface IBasicVaccinationModel {
  id: string;
  vaccinations: string[];
  vaccine?: string;
  plannedNextShot?: UTCDate;
  plannedFinalShot?: UTCDate;
  comments?: string;
  vaccinationStatus: string;
}

export interface ICandidateOSModel {
  title?: string;
  salutation: string;
  firstname: string;
  lastname: string;
  birthname?: string;
  email?: string;
  phone?: string;
  mobilePhone?: string;
  immigrationCountryPhone?: string;
  birthdate?: UTCDate;
  martialStatus?: string;
  maritalStatusSince?: UTCDate;
  numberOfChildren?: number;
  citizenship?: string;
  countryOfOrigin?: string;
  countryOfHabitualResidence?: string;
  countryOfBirth?: string;
  cityOfBirth?: string;
  passNumber?: string;
  passAuthority?: string;
  idCardNumber?: string;
  taxNumber?: string;
  taxId?: string;
  socSecNumber?: string;
  healthInsuranceCompanyName?: string;
  healthInsuranceNumber?: string;
  hasReceivedHealthInsuranceCard?: boolean;
  hasLiabilityInsurance?: boolean;
  liabilityInsuranceNumber?: string;
  liabilityInsuranceCompanyName?: string;
  drivingLicense?: string[];
  professionalDriver?: boolean;

  addresses?: IAddressModel[];
  qualifications?: IQualificationModel[];
  noQualificationsAvailable?: boolean;
  furtherEducations?: IFurtherEducation[];
  noExperienceAvailable?: boolean;
  noFurtherEducationAvailable?: boolean;

  experiences?: IExperienceModel[];
  totalExperienceInMonth?: number;
  languageSkills?: ILanguageSkillModel[];

  preferredCareFacility?: string[];
  preferredCareFacilityFlexibility?: boolean;
  preferredWorkEnvironment?: string[];
  preferredWorkEnvironmentFlexibility?: boolean;
  preferredProfessions?: string[];
  preferredWorkingField?: string[];
  preferredWorkingFieldFlexibility?: boolean;
  preferredLocationState?: string[];
  preferredLocationStateFlexibility?: boolean;
  preferredCity?: string[];
  preferredCityFlexibility?: boolean;
  preferredCandidates?: string;
  preferredPathOfRecognition?: string;
  preferredLanguageLevelForInterview?: string;
  preferredLanguageLevelForTransfer?: string;
  preferredStartDate?: UTCDate;
  preferredEmploymentType?: string;
  preferredTemporalScope?: string;
  preferredTypeOfImmigration?: string;
  preferredFamilyReunion?: string;

  vaccinations?: IBasicVaccinationModel[];

  customFields?: any;

  other?: IOtherModel;
  externalCharacteristics?: IExternalCharacteristicsModel;

  bankDetails?: IBankDetailsModel[];

  competencies?: ICompetenciesModel;
}

export interface IDateWithTime {
  date?: DateOrDateTime;
  hasTime?: boolean;
}

export enum AddressType {
  IdAddress = "ID-ADDRESS",
  CountryOfOriginAddress = "COUNTRY-OF-ORIGIN-ADDRESS",
  CurrentAddress = "CURRENT-ADDRESS",
  ImmigrationCountryAddress = "IMMIGRATION-COUNTRY-ADDRESS",
}

export interface IInternalModel {
  costCenter?: string;
  languageSchooldId?: string;
  sourceOrganizationId?: string;
  placementPartnerOrganizationIds?: string[];
  potentialEmployerOrganizationIds?: string[];
  preferredEmployerOrganizationIds?: string[];
  category?: string;
  notes?: string;
}

export enum ExperienceType {
  Normal = "Normal",
  OtherActivities = "OtherActivities",
}

export enum KnowledgeProficiency {
  None = "None",
  Basic = "Basic",
  Expert = "Expert",
  Specialist = "Specialist",
}

export enum YearsOfSkillExperience {
  NoExperience = "NoExperience",
  LessThanOne = "LessThanOne",
  BetweenOneAndThree = "BetweenOneAndThree",
  MoreThanThree = "MoreThanThree",
  MoreThanFive = "MoreThanFive",
}

export enum LanguageTrainingStatus {
  Started = "Started",
  RegisterToLanguageCourse = "RegisterToLanguageCourse",
  Registered = "Registered",
  Paused = "Paused",
  Finished = "Finished",
  Certified = "Certified",
  Canceled = "Canceled",
}

export enum LanguageLevelSource {
  CurriculumVitaeNotValidated = "CurriculumVitaeNotValidated",
  CurriculumVitae = "CurriculumVitae",
  LanguageCourse = "LanguageCourse",
  PlacementTest = "PlacementTest",
  UploadedCertificate = "UploadedCertificate",
  EducationModule = "EducationModule",
}

export enum EyeColor {
  Blue = "Blue",
  Brown = "Brown",
  Green = "Green",
  Gray = "Gray",
}

export enum TypeOfEmployment {
  Freelancer = "Freelancer",
  Notrelevant = "NotRelevant",
  Shiftsystem = "Shiftsystem",
  PartTimeJob = "PartTimeJob",
}
